import { useEffect, useState } from 'react';
import { CalendarCheck2, Loader2 } from 'lucide-react';
import { adminApi } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationPrevious,
  PaginationNext,
} from '@/components/ui/pagination';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const formatDateOnly = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

const getPriorityColor = (priority) => {
  const colors = {
    low: 'bg-gray-100 text-gray-700',
    medium: 'bg-yellow-100 text-yellow-800',
    high: 'bg-red-100 text-red-700'
  };
  return colors[priority] || colors.medium;
};

const getStatusColor = (status) => {
  const colors = {
    pending: 'bg-blue-100 text-blue-800',
    completed: 'bg-green-100 text-green-800'
  };
  return colors[status] || colors.pending;
};

export default function TasksPage() {
  const [tasks, setTasks] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [remindingTaskId, setRemindingTaskId] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [staffFilter, setStaffFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [showCommentsDialog, setShowCommentsDialog] = useState(false);
  const [completeRemarks, setCompleteRemarks] = useState('');
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedCommentsTask, setSelectedCommentsTask] = useState(null);

  const [form, setForm] = useState({
    title: '',
    message: '',
    due_date: '',
    priority: 'medium',
    assigned_to_id: ''
  });

  useEffect(() => {
    fetchStaff();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, staffFilter]);

  useEffect(() => {
    fetchTasks();
  }, [statusFilter, staffFilter, page]);

  const fetchStaff = async () => {
    try {
      const response = await adminApi.getSalesStaff();
      setStaff(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch sales staff');
    }
  };

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        page_size: pageSize,
      };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (staffFilter !== 'all') params.assigned_to_id = staffFilter;

      const response = await adminApi.getTasks(params);
      setTasks(response.data.items || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error('Failed to fetch tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!form.title.trim()) {
      toast.error('Please enter a task title');
      return;
    }
    if (!form.assigned_to_id) {
      toast.error('Please select a staff member');
      return;
    }

    setSaving(true);
    try {
      const response = await adminApi.createTask({
        title: form.title.trim(),
        message: form.message.trim(),
        due_date: form.due_date || undefined,
        priority: form.priority,
        assigned_to_id: form.assigned_to_id
      });
      toast.success(response.data?.message || 'Task created');
      setForm({ title: '', message: '', due_date: '', priority: 'medium', assigned_to_id: '' });
      fetchTasks();
    } catch (error) {
      toast.error('Failed to create task');
    } finally {
      setSaving(false);
    }
  };

  const handleSendReminder = async (taskId) => {
    setRemindingTaskId(taskId);
    try {
      const response = await adminApi.sendTaskReminder(taskId);
      toast.success(response.data?.message || 'Task reminder sent');
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setRemindingTaskId('');
    }
  };

  const handleComplete = async (taskId) => {
    try {
      await adminApi.completeTask(taskId, { remarks: completeRemarks });
      toast.success('Task completed');
      setShowCompleteDialog(false);
      setCompleteRemarks('');
      setSelectedTask(null);
      fetchTasks();
    } catch (error) {
      toast.error('Failed to complete task');
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-tasks-page">
      <div>
        <h1 className="text-3xl font-bold text-[#2D2420]">Tasks</h1>
        <p className="text-[#A8A8A8] mt-1">Assign tasks to sales staff and track completion</p>
      </div>

      <Card className="bg-white border-[#E5E5E5]">
        <CardContent className="p-4 space-y-4">
          <div>
            <p className="text-sm font-semibold text-[#2D2420]">Create Task</p>
            <p className="text-xs text-[#A8A8A8]">Assign a task with due date and priority</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Title</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Task title"
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Assign To</Label>
              <Select value={form.assigned_to_id} onValueChange={(v) => setForm({ ...form, assigned_to_id: v })}>
                <SelectTrigger className="border-[#E5E5E5]">
                  <SelectValue placeholder="Select staff" />
                </SelectTrigger>
                <SelectContent>
                  {staff.map((member) => (
                    <SelectItem key={member.id} value={member.id}>{member.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-[#5C4033]">Message</Label>
            <Textarea
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="Task details"
              className="border-[#E5E5E5] focus:border-[#C97A5E]"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Due Date</Label>
              <Input
                type="date"
                value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger className="border-[#E5E5E5]">
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button
                onClick={handleCreate}
                disabled={saving}
                className="bg-[#C97A5E] hover:bg-[#B06850]"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Create Task
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border-[#E5E5E5]">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-48 border-[#E5E5E5]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={staffFilter} onValueChange={setStaffFilter}>
              <SelectTrigger className="w-full md:w-56 border-[#E5E5E5]">
                <SelectValue placeholder="Assigned staff" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Staff</SelectItem>
                {staff.map((member) => (
                  <SelectItem key={member.id} value={member.id}>{member.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border-[#E5E5E5]">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-[#C97A5E]" />
            </div>
          ) : tasks.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-[#A8A8A8]">
              <CalendarCheck2 className="w-12 h-12 mb-4" />
              <p>No tasks found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                  <tr>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Title</th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Assigned</th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Due</th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Priority</th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Status</th>
                    <th className="text-right py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E5E5]">
                  {tasks.map((task) => (
                    <tr key={task.id} className="hover:bg-[#FAFAF9]/50 transition-colors">
	                      <td className="py-4 px-6">
	                        <div className="space-y-1">
	                          <p className="font-medium text-[#2D2420]">{task.title}</p>
	                          {task.message ? (
	                            <p className="text-xs text-[#A8A8A8] truncate max-w-xs">{task.message}</p>
	                          ) : null}
                            {task.last_reminder_sent_at ? (
                              <p className="text-[11px] text-[#C97A5E]">
                                Reminder sent on {new Date(task.last_reminder_sent_at).toLocaleString('en-IN', {
                                  day: 'numeric',
                                  month: 'short',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </p>
                            ) : null}
	                        </div>
	                      </td>
                      <td className="py-4 px-6 text-[#2D2420]">{task.assigned_to_name || '-'}</td>
                      <td className="py-4 px-6 text-[#A8A8A8]">{formatDateOnly(task.due_date)}</td>
                      <td className="py-4 px-6">
                        <Badge className={cn('text-xs', getPriorityColor(task.priority))}>{task.priority}</Badge>
                      </td>
                      <td className="py-4 px-6">
                        <Badge className={cn('text-xs', getStatusColor(task.status))}>{task.status}</Badge>
                      </td>
                      <td className="py-4 px-6 text-right">
                        <div className="flex items-center justify-end gap-2">
	                          {task.status !== 'completed' ? (
                                <>
	                            <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleSendReminder(task.id)}
                                  disabled={remindingTaskId === task.id}
                                >
                                  {remindingTaskId === task.id ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                                  Send Reminder
                                </Button>
	                            <Button
	                              variant="outline"
	                              size="sm"
	                              onClick={() => {
	                                setSelectedTask(task);
	                                setShowCompleteDialog(true);
	                              }}
	                            >
	                              Mark Complete
	                            </Button>
                                </>
	                          ) : (
                            <span className="text-xs text-[#A8A8A8]">Completed</span>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedCommentsTask(task);
                              setShowCommentsDialog(true);
                            }}
                          >
                            View Comments
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
        {!loading && total > 0 && (
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-6 py-4 border-t border-[#E5E5E5]">
            <p className="text-sm text-[#A8A8A8]">
              Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, total)} of {total}
            </p>
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={(e) => {
                      e.preventDefault();
                      setPage((prev) => Math.max(1, prev - 1));
                    }}
                    className={page === 1 ? 'pointer-events-none opacity-50' : ''}
                  />
                </PaginationItem>
                <PaginationItem>
                  <span className="px-3 text-sm text-[#5C4033]">
                    Page {page} of {Math.max(1, Math.ceil(total / pageSize))}
                  </span>
                </PaginationItem>
                <PaginationItem>
                  <PaginationNext
                    onClick={(e) => {
                      e.preventDefault();
                      setPage((prev) => Math.min(Math.max(1, Math.ceil(total / pageSize)), prev + 1));
                    }}
                    className={page >= Math.max(1, Math.ceil(total / pageSize)) ? 'pointer-events-none opacity-50' : ''}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </Card>
      <Dialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#2D2420]">Complete Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-[#A8A8A8]">Add remarks about the action taken.</p>
            <Textarea
              value={completeRemarks}
              onChange={(e) => setCompleteRemarks(e.target.value)}
              placeholder="Remarks"
              className="border-[#E5E5E5] focus:border-[#C97A5E]"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCompleteDialog(false);
                setCompleteRemarks('');
                setSelectedTask(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => selectedTask && handleComplete(selectedTask.id)}
              className="bg-[#C97A5E] hover:bg-[#B06850]"
              disabled={!selectedTask}
            >
              Mark Complete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showCommentsDialog} onOpenChange={setShowCommentsDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#2D2420]">Task Comments</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-[#A8A8A8]">
              {selectedCommentsTask?.completed_note ? 'Completion remarks' : 'No comments added yet.'}
            </p>
            {selectedCommentsTask?.completed_note ? (
              <div className="rounded-lg border border-[#E5E5E5] bg-[#FAFAF9] p-3 text-sm text-[#2D2420]">
                {selectedCommentsTask.completed_note}
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCommentsDialog(false);
                setSelectedCommentsTask(null);
              }}
              className="border-[#E5E5E5]"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
